import { apiFetcher } from '@/api/api-fetcher'
import { logger } from '@/utils/logger'
import type {
  RegisteredQuestionListResponse,
  RegisteredQuestionGroupedResponse,
  PromptGenerationStartResponse,
  PromptGenerationJobStatusResponse,
  GenerationJobListResponse,
  ManualPromptCreateResponse,
  BulkPromptUpdateResponse,
  BulkPromptDeleteResponse,
  PromptStatsResponse,
  KeywordAnalysisResponse,
  KeywordGroupResponse,
  RegistrationBatchesResponse,
  RegistrationBatchPromptsResponse,
} from '@/types/api/responses/prompt'
import type {
  PromptGenerationRequest,
  ManualPromptCreateRequest,
  BulkPromptUpdateRequest,
  UpdatePromptRequest,
  BulkPromptDeleteRequest,
  SinglePromptDeleteRequest,
  ImportPromptsRequest,
  RegisteredQuestionQueryParams,
  GenerationJobQueryParams,
  PromptStatsQueryParams,
  KeywordAnalysisQueryParams,
  KeywordGroupQueryParams,
} from '@/types/api/requests/prompt'

// プロンプト一覧取得（Questions統合版）
export async function getPrompts(
  spaceId: string,
  query?: RegisteredQuestionQueryParams
): Promise<RegisteredQuestionListResponse | RegisteredQuestionGroupedResponse> {
  try {
    console.log('🔍 getPrompts API call:', {
      spaceId,
      query,
      url: `/spaces/${spaceId}/registered-questions`,
    })

    const response = await apiFetcher.get(`/spaces/${spaceId}/registered-questions`, {
      params: query as unknown as Record<string, unknown>,
    })

    console.log('🔍 getPrompts API response:', response)
    return response as RegisteredQuestionListResponse | RegisteredQuestionGroupedResponse
  } catch (error) {
    console.error('❌ getPrompts API error:', error)
    throw error
  }
}

// プロンプト詳細取得
export async function getPrompt(
  spaceId: string,
  questionId: string
): Promise<{ question: import('@/types/api/responses/prompt').RegisteredQuestion }> {
  return apiFetcher.get(`/spaces/${spaceId}/registered-questions/${questionId}`)
}

// プロンプト生成ジョブ開始
export async function startPromptGeneration(
  spaceId: string,
  data: PromptGenerationRequest['body']
): Promise<PromptGenerationStartResponse> {
  return apiFetcher.post(`/spaces/${spaceId}/registered-questions/generate`, data)
}

// 生成ジョブステータス取得
export async function getGenerationJobStatus(
  spaceId: string,
  jobId: string
): Promise<PromptGenerationJobStatusResponse> {
  return apiFetcher.get(`/spaces/${spaceId}/registered-questions/generation-jobs/${jobId}`)
}

// 生成ジョブ一覧取得
export async function getGenerationJobs(
  spaceId: string,
  query?: GenerationJobQueryParams
): Promise<GenerationJobListResponse> {
  return apiFetcher.get(`/spaces/${spaceId}/registered-questions/generation-jobs`, {
    params: query as unknown as Record<string, unknown>,
  })
}

// 生成ジョブキャンセル
export async function cancelGenerationJob(
  spaceId: string,
  jobId: string
): Promise<{
  success: boolean
  message: string
  jobId: string
  finalStatus: 'CANCELLED' | 'COMPLETED'
}> {
  return apiFetcher.delete(`/spaces/${spaceId}/registered-questions/generation-jobs/${jobId}`)
}

// プロンプト手動作成
export async function createManualPrompts(
  spaceId: string,
  data: ManualPromptCreateRequest['body']
): Promise<ManualPromptCreateResponse> {
  return apiFetcher.post(`/spaces/${spaceId}/registered-questions`, data)
}

// プロンプト一括更新
export async function updatePromptsInBulk(
  spaceId: string,
  data: BulkPromptUpdateRequest['body']
): Promise<BulkPromptUpdateResponse> {
  return apiFetcher.patch(`/spaces/${spaceId}/registered-questions/bulk`, data)
}

// プロンプト個別更新
export async function updatePrompt(
  spaceId: string,
  promptId: string,
  data: UpdatePromptRequest['body']
): Promise<{
  message: string
  prompt: {
    id: string
    text: string
    keyword?: string
    nominationType?: string
    searchIntents?: Array<'know' | 'go' | 'do' | 'buy' | 'other'>
    searchVolume?: number
    referenceWebPages: string[]
    updatedAt: string
  }
}> {
  return apiFetcher.patch(`/spaces/${spaceId}/registered-questions/${promptId}`, data)
}

// プロンプト一括削除
export async function deletePromptsInBulk(
  spaceId: string,
  data: BulkPromptDeleteRequest['body']
): Promise<BulkPromptDeleteResponse> {
  return apiFetcher.delete(`/spaces/${spaceId}/registered-questions/bulk`, { body: data })
}

// プロンプト統計取得
export async function getPromptStats(
  spaceId: string,
  query?: PromptStatsQueryParams
): Promise<PromptStatsResponse> {
  return apiFetcher.get(`/spaces/${spaceId}/registered-questions/stats`, {
    params: query as unknown as Record<string, unknown>,
  })
}

// キーワード分析取得
export async function getKeywordAnalysis(
  spaceId: string,
  query?: KeywordAnalysisQueryParams
): Promise<KeywordAnalysisResponse> {
  return apiFetcher.get(`/spaces/${spaceId}/registered-questions/keyword-analysis`, {
    params: query as unknown as Record<string, unknown>,
  })
}

// 単体削除
export async function deletePrompt(
  spaceId: string,
  { params, query }: SinglePromptDeleteRequest
): Promise<void> {
  const searchParams = new URLSearchParams()
  if (query?.deleteAnalysisResults != null) {
    searchParams.set('deleteAnalysisResults', String(query.deleteAnalysisResults))
  }
  const qs = searchParams.toString()
  const url = `/spaces/${spaceId}/registered-questions/${params.id}${qs ? `?${qs}` : ''}`
  return apiFetcher.delete(url)
}

// インポート（JSON）
export async function importPrompts(
  spaceId: string,
  data: ImportPromptsRequest['body'] | FormData
): Promise<{
  message: string
  summary: { created: number; updated: number; skipped: number; failed: number }
  errors: Array<{ row: number; reason: string }>
}> {
  return apiFetcher.post(`/spaces/${spaceId}/registered-questions/import`, data)
}

// キーワードグループ取得
export async function getKeywordGroups(
  spaceId: string,
  serviceId: string,
  query?: KeywordGroupQueryParams
): Promise<KeywordGroupResponse> {
  return apiFetcher.get(
    `/spaces/${spaceId}/services/${serviceId}/registered-questions/keyword-groups`,
    {
      params: query as unknown as Record<string, unknown>,
    }
  )
}

// 特定キーワードのプロンプト取得（遅延読み込み用）
export async function getPromptsForKeyword(
  spaceId: string,
  serviceId: string,
  keyword: string | null,
  query?: {
    nominationType?: 'BRANDED' | 'NON_BRANDED' | 'OTHER'
    searchIntents?: Array<'know' | 'go' | 'do' | 'buy' | 'other'>
    kwSearchIntents?: Array<'KNOW' | 'GO' | 'DO' | 'BUY' | 'OTHER' | 'NO_KW'>
    registrationType?: string
    generationModel?: string
    language?: string
    tags?: string[]
    tagFilterMode?: 'AND' | 'OR'
    page?: number
    limit?: number
  }
): Promise<{
  keyword: string | null
  prompts: import('@/types/api/responses/prompt').RegisteredQuestion[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}> {
  // nullキーワードは特殊値 "no_keyword" として送信
  const keywordParam = keyword === null || keyword === '' ? 'no_keyword' : keyword

  logger.debug('API_DEBUG: getPromptsForKeyword', {
    spaceId,
    serviceId,
    keyword,
    keywordParam,
    query,
  })

  const url = `/spaces/${spaceId}/services/${serviceId}/registered-questions/keyword-groups/${encodeURIComponent(keywordParam)}/prompts`
  logger.debug('API_DEBUG: Making request to URL', url)
  logger.debug('API_DEBUG: Query parameters', query)

  return apiFetcher.get(url, {
    params: query as unknown as Record<string, unknown>,
  })
}

// 全プロンプトCSVエクスポート
export async function exportAllPrompts(
  spaceId: string,
  serviceId: string,
  language?: string
): Promise<Blob> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'
  const params = new URLSearchParams({ serviceId })
  if (language) {
    params.set('lang', language)
  }
  const url = `${baseUrl}/spaces/${spaceId}/registered-questions/export?${params.toString()}`

  // Get authentication token using Firebase
  const { getAuth } = await import('firebase/auth')
  const auth = getAuth()

  if (!auth.currentUser) {
    throw new Error('User not authenticated')
  }

  const token = await auth.currentUser.getIdToken()

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return response.blob()
}

// フィルター付きプロンプトCSVエクスポート
export async function exportFilteredPrompts(
  spaceId: string,
  serviceId: string,
  filters: {
    registrationType?: string
    keyword?: string
    nominationType?: string
    searchIntents?: string[]
    kwSearchIntents?: string[]
    model?: string
    language?: string
    generationJobId?: string
    registrationBatchId?: string
    dateFrom?: string
    dateTo?: string
    tags?: string[]
    tagFilterMode?: 'AND' | 'OR'
  },
  language?: string
): Promise<Blob> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'

  // Build query parameters
  const searchParams = new URLSearchParams()
  searchParams.set('serviceId', serviceId)

  if (filters.registrationType) searchParams.set('registrationType', filters.registrationType)
  if (filters.keyword) searchParams.set('keyword', filters.keyword)
  if (filters.nominationType) searchParams.set('nominationType', filters.nominationType)
  if (filters.searchIntents?.length)
    searchParams.set('searchIntents', filters.searchIntents.join(','))
  if (filters.kwSearchIntents?.length)
    searchParams.set('kwSearchIntents', filters.kwSearchIntents.join(','))
  if (filters.model) searchParams.set('model', filters.model)
  if (filters.language) searchParams.set('language', filters.language)
  if (filters.generationJobId) searchParams.set('generationJobId', filters.generationJobId)
  if (filters.registrationBatchId)
    searchParams.set('registrationBatchId', filters.registrationBatchId)
  if (filters.dateFrom) searchParams.set('dateFrom', filters.dateFrom)
  if (filters.dateTo) searchParams.set('dateTo', filters.dateTo)
  if (filters.tags?.length) searchParams.set('tags', filters.tags.join(','))
  if (filters.tagFilterMode) searchParams.set('tagFilterMode', filters.tagFilterMode)
  if (language) searchParams.set('lang', language)

  const url = `${baseUrl}/spaces/${spaceId}/registered-questions/export/filtered?${searchParams.toString()}`

  // Get authentication token using Firebase
  const { getAuth } = await import('firebase/auth')
  const auth = getAuth()

  if (!auth.currentUser) {
    throw new Error('User not authenticated')
  }

  const token = await auth.currentUser.getIdToken()

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return response.blob()
}

// Registration batch endpoints
export async function getRegistrationBatches(
  spaceId: string,
  query?: {
    serviceId?: string
    registrationType?: string
    nominationType?: 'BRANDED' | 'NON_BRANDED' | 'OTHER'
    searchIntents?: Array<'KNOW' | 'GO' | 'DO' | 'BUY' | 'OTHER'>
    dateFrom?: string
    dateTo?: string
    page?: number
    limit?: number
    sortBy?: 'createdAt' | 'promptCount' | 'registrationBatchId'
    sortDirection?: 'asc' | 'desc'
  }
): Promise<RegistrationBatchesResponse> {
  return apiFetcher.get(`/spaces/${spaceId}/registered-questions/registration-batches`, {
    params: query as unknown as Record<string, unknown>,
  })
}

export async function getPromptsByRegistrationBatch(
  spaceId: string,
  batchId: string,
  query?: {
    serviceId?: string
    nominationType?: 'BRANDED' | 'NON_BRANDED' | 'OTHER'
    searchIntents?: Array<'KNOW' | 'GO' | 'DO' | 'BUY' | 'OTHER'>
    registrationType?: string
    keyword?: string
    kwSearchIntents?: Array<'KNOW' | 'GO' | 'DO' | 'BUY' | 'OTHER' | 'NO_KW'>
    tags?: string[]
    tagFilterMode?: 'AND' | 'OR'
    page?: number
    limit?: number
    orderBy?: string
    orderDirection?: 'asc' | 'desc'
  }
): Promise<RegistrationBatchPromptsResponse> {
  // Handle null batch ID as special case
  const batchParam = batchId === null || batchId === '' ? 'no-batch' : batchId

  return apiFetcher.get(
    `/spaces/${spaceId}/registered-questions/registration-batches/${encodeURIComponent(batchParam)}/prompts`,
    {
      params: query as unknown as Record<string, unknown>,
    }
  )
}

export async function getGenerationConfig(spaceId: string) {
  return apiFetcher.get(`/spaces/${spaceId}/registered-questions/generation-config`)
}

// ============================================================================
// Prompt Analysis API Functions (Phase 2)
// ============================================================================

/**
 * Get prompts with execution counts for analysis view
 * Groups by Question.text and aggregates execution data
 */
export async function getPromptsWithExecutionCounts(
  spaceId: string,
  serviceId: string,
  query?: import('@/types/prompt-analysis').PromptsListQueryParams
): Promise<import('@/types/prompt-analysis').PromptsWithExecutionResponse> {
  const { filters, ...rest } = query ?? {}
  const flattenedParams: Record<string, unknown> = {
    ...rest,
    ...(filters ?? {}),
  }

  return apiFetcher.get(
    `/spaces/${spaceId}/services/${serviceId}/prompts/grouped-with-executions`,
    {
      params: flattenedParams,
    }
  )
}

/**
 * Get execution history for a specific prompt (recent 10 by default)
 */
export async function getPromptExecutionHistory(
  spaceId: string,
  serviceId: string,
  promptId: string,
  limit: number = 10
): Promise<import('@/types/prompt-analysis').PromptExecutionHistoryResponse> {
  return apiFetcher.get(
    `/spaces/${spaceId}/services/${serviceId}/prompts/${promptId}/execution-history`,
    {
      params: { limit },
    }
  )
}

/**
 * Get analysis summary for a specific prompt (multi-model aggregation)
 */
export async function getPromptAnalysisSummary(
  spaceId: string,
  serviceId: string,
  promptId: string
): Promise<import('@/types/prompt-analysis').PromptAnalysisSummaryResponse> {
  return apiFetcher.get(
    `/spaces/${spaceId}/services/${serviceId}/prompts/${promptId}/analysis-summary`
  )
}

/**
 * Get score trends over time for a specific prompt
 */
export async function getPromptScoreTrends(
  spaceId: string,
  serviceId: string,
  promptId: string,
  query: import('@/types/prompt-analysis').ScoreTrendsQueryParams
): Promise<import('@/types/prompt-analysis').PromptScoreTrendsResponse> {
  return apiFetcher.get(
    `/spaces/${spaceId}/services/${serviceId}/prompts/${promptId}/score-trends`,
    {
      params: query as unknown as Record<string, unknown>,
    }
  )
}

/**
 * Get aggregated score summary for a specific prompt (server-side aggregation)
 */
export async function getPromptScoreSummary(
  spaceId: string,
  serviceId: string,
  promptId: string
): Promise<import('@/types/prompt-analysis').PromptScoreSummaryResponse> {
  return apiFetcher.get(
    `/spaces/${spaceId}/services/${serviceId}/prompts/${promptId}/score-summary`
  )
}

/**
 * Get model score comparison with server-side Bayesian averages
 */
export async function getModelScoreComparison(
  spaceId: string,
  serviceId: string,
  promptId: string
): Promise<import('@/types/prompt-analysis').PromptModelScoreComparisonResponse> {
  return apiFetcher.get(
    `/spaces/${spaceId}/services/${serviceId}/prompts/${promptId}/model-score-comparison`
  )
}

/**
 * Get service-level score trends per model with statistical indicators
 */
export async function getServiceScoreTrends(
  spaceId: string,
  serviceId: string,
  promptId: string,
  scoreType: import('@/types/prompt-analysis').ScoreType,
  granularity?: 'day' | 'month'
): Promise<import('@/types/prompt-analysis').ServiceScoreTrendsResponse> {
  return apiFetcher.get(
    `/spaces/${spaceId}/services/${serviceId}/prompts/${promptId}/service-score-trends`,
    {
      params: {
        scoreType,
        granularity: granularity || 'month',
      } as unknown as Record<string, unknown>,
    }
  )
}
